import {Component, DestroyRef, effect, inject, Input, OnInit} from "@angular/core";
import {BaseAutocompleteDirective} from "../base-autocomplete.directive";
import {FormControl, Validators} from "@angular/forms";
import {FetchData_TypeMapping} from "../autocomplete.service";
import {SubscriptSizing} from "@angular/material/form-field";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {startWith} from "rxjs/operators";

@Component({
	selector: "sim-chip-autocomplete",
	templateUrl: "./sim-chip-autocomplete.component.html",
	styleUrl: "./sim-chip-autocomplete.component.scss",
	providers: [{provide: BaseAutocompleteDirective, useExisting: SimChipAutocompleteComponent}],
	standalone: false,
})
export class SimChipAutocompleteComponent<T extends keyof FetchData_TypeMapping> extends BaseAutocompleteDirective<T> implements OnInit {
	// I/O
	@Input() subscriptSizing: SubscriptSizing = "fixed";

	// For housekeeping in the administration-table-advanced-search component
	@Input() id: string | null = null;

	protected readonly Validators = Validators;
	protected readonly errorCtrl = new FormControl<FetchData_TypeMapping[T] | null>(this.formCtrl()?.value ?? null);

	// Injections
	readonly #destroyRef = inject(DestroyRef);

	public onChipClick(): void {
		this.autocompleteService.openAutocompleteDetailsDialog(this.dataType(), this.formCtrl()!.value, {})?.then();
	}

	/**
	 * Due to the default chip-grid behavior, which propagates the values of the chips to the bound formControl on blur events,
	 * we need to have a separate errorCtrl that mirrors the state of the parent directive formCtrl.
	 *
	 * Otherwise, whenever the user clicks away from this component, an illegal value is propagated to the BaseAutocompleteDirective.formCtrl
	 *
	 * @private
	 */
	readonly #inheritFormState = effect(() => {
		this.formCtrl()
			?.valueChanges.pipe(takeUntilDestroyed(this.#destroyRef), startWith(this.formCtrl()?.value))
			.subscribe((value) => this.errorCtrl.setValue(value!));
		this.formCtrl()
			?.statusChanges.pipe(takeUntilDestroyed(this.#destroyRef), startWith(this.formCtrl()?.status))
			.subscribe((status) => {
				this.errorCtrl.setErrors(status === "INVALID" && this.formCtrl()?.touched ? {invalid: true} : null);

				if (status === "DISABLED") {
					this.errorCtrl.disable();
				} else if (this.errorCtrl.disabled) {
					this.errorCtrl.enable();
				}
			});
	});
}
